import React, { useState, useEffect } from 'react';
import apiService from '../services/api';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext'; // <-- IMPORT useAuth
import './Admin/ProductEditModal.css';

const toDateTimeInputValue = (dateValue) => {
  if (!dateValue) return '';
  const date = new Date(dateValue);
  if (Number.isNaN(date.getTime())) return '';
  const offsetDate = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return offsetDate.toISOString().slice(0, 16);
};

const ConsultantActionModal = ({ consultation, consultantProfile, isOpen, onClose, onRefresh }) => {
  const { user } = useAuth(); // <-- Get current logged in user to identify "My" messages
  const [activeTab, setActiveTab] = useState('meeting'); // 'meeting', 'payment_request', 'verify_proof', 'chat'
  
  // Tab 1: Meeting & Status
  const [meetingData, setMeetingData] = useState({ scheduledAt: '', meetingLink: '', consultantNotes: '', remediesSummary: '', status: '' });
  
  // Tab 2: Payment Request
  const [paymentReq, setPaymentReq] = useState({ amount: '', upiId: '', paymentLink: '', notes: '' });
  const [qrFile, setQrFile] = useState(null);
  
  const [isSaving, setIsSaving] = useState(false);

  // --- NEW STATE FOR CHAT ---
  const [messageText, setMessageText] = useState('');
  const [messageFile, setMessageFile] = useState(null);
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  // --------------------------

  useEffect(() => {
    if (isOpen && consultation) {
      setMeetingData({
        scheduledAt: toDateTimeInputValue(consultation.scheduledAt),
        meetingLink: consultation.meetingLink || '',
        consultantNotes: consultation.consultantNotes || '',
        remediesSummary: consultation.remediesSummary || '',
        status: consultation.status || 'under_review'
      });
      setPaymentReq({
        amount: consultation.paymentInstructions?.amount || consultation.payment?.amount || '',
        upiId: consultation.paymentInstructions?.upiId || '',
        paymentLink: consultation.paymentInstructions?.paymentLink || '',
        notes: consultation.paymentInstructions?.notes || ''
      });
      setQrFile(null);
      
      // Clear chat drafts when opening
      setMessageText('');
      setMessageFile(null);
      
      // Auto-switch to verify tab if customer submitted proof
      if (consultation.status === 'payment_submitted' && consultation.paymentProof?.verificationStatus === 'pending') {
        setActiveTab('verify_proof');
      } else {
        setActiveTab('meeting');
      }
    }
  }, [consultation, isOpen]);

  if (!isOpen || !consultation) return null;

  // --- Handlers for Meeting Tab ---
  const handleMeetingSave = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    const payload = { ...meetingData };
    payload.scheduledAt = meetingData.scheduledAt ? new Date(meetingData.scheduledAt).toISOString() : null;
    
    try {
      await apiService.updateVastuConsultantWork(consultation._id, payload);
      toast.success('Meeting & Status updated.');
      onRefresh();
      onClose();
    } catch (err) { toast.error(err.message || 'Failed to update.'); }
    finally { setIsSaving(false); }
  };

  // --- Handlers for Payment Request Tab ---
  const loadDefaults = () => {
    const defaults = consultantProfile?.defaultPaymentInstructions || {};
    setPaymentReq(prev => ({
      ...prev,
      upiId: defaults.upiId || '',
      paymentLink: defaults.paymentLink || '',
      notes: defaults.notes || ''
    }));
    toast.success('Defaults loaded. Click save to apply.');
  };

  const handlePaymentReqSave = async (e, saveAsDefault = false) => {
    e.preventDefault();
    setIsSaving(true);
    const formData = new FormData();
    formData.append('amount', paymentReq.amount);
    formData.append('upiId', paymentReq.upiId);
    formData.append('paymentLink', paymentReq.paymentLink);
    formData.append('notes', paymentReq.notes);
    if (qrFile) formData.append('qrCode', qrFile);

    try {
      // 1. Save to this specific consultation
      await apiService.updateConsultantPaymentInstructions(consultation._id, formData);
      
      // 2. If checked, also save to consultant's master profile
      if (saveAsDefault) {
        const defaultFormData = new FormData();
        defaultFormData.append('upiId', paymentReq.upiId);
        defaultFormData.append('paymentLink', paymentReq.paymentLink);
        defaultFormData.append('notes', paymentReq.notes);
        if (qrFile) defaultFormData.append('qrCode', qrFile);
        await apiService.updateMyVastuDefaultPayment(defaultFormData);
      }
      
      toast.success(saveAsDefault ? 'Payment details saved and set as default!' : 'Payment details sent to customer.');
      onRefresh();
    } catch (err) { toast.error(err.message || 'Failed to send instructions.'); }
    finally { setIsSaving(false); }
  };

  // --- Handlers for Verification Tab ---
  const handleVerify = async (action) => {
    setIsSaving(true);
    try {
      await apiService.verifyConsultantPaymentProof(consultation._id, { action });
      toast.success(`Payment ${action}.`);
      onRefresh();
      onClose();
    } catch (err) { toast.error(err.message || 'Failed to verify.'); }
    finally { setIsSaving(false); }
  };

  // --- File Download Helper ---
  const downloadAsset = async (assetType, fileName, fileObj = null) => {
    try { 
      if (assetType === 'attachment' && fileObj) {
        // Chat attachment download
        await apiService.downloadConsultantFile(consultation._id, fileObj._id, fileName);
      } else {
        // Standard payment asset download (qr or proof)
        await apiService.downloadConsultantPaymentAsset(consultation._id, assetType, fileName); 
      }
    }
    catch (err) { toast.error('Failed to download.'); }
  };

  // --- NEW HANDLER FOR SENDING CHAT MESSAGE ---
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!messageText.trim() && !messageFile) {
        toast.error("Please enter a message or attach a file.");
        return;
    }

    setIsSendingMessage(true);
    const formData = new FormData();
    if (messageText.trim()) formData.append('text', messageText.trim());
    if (messageFile) formData.append('attachment', messageFile);

    try {
        await apiService.addVastuConsultationMessage(consultation._id, formData);
        toast.success("Message sent.");
        setMessageText('');
        setMessageFile(null);
        onRefresh(); // Refresh the parent component to get the new messages list
    } catch (err) {
        toast.error(err.message || "Failed to send message.");
    } finally {
        setIsSendingMessage(false);
    }
  };
  // --------------------------------------------

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px' }}>
        <div className="modal-header">
          <div>
            <h3 style={{ margin: 0 }}>Manage: {consultation.bookingCode}</h3>
            <span style={{ fontSize: '0.85rem', color: '#666' }}>Customer: {consultation.customer?.fullName}</span>
          </div>
          <button className="close-btn" onClick={onClose}>&times;</button>
        </div>

        {/* Custom Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid #ddd', background: '#f8f9fa', overflowX: 'auto' }}>
          {['meeting', 'payment_request', 'verify_proof', 'chat'].map(tab => (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab)}
              style={{
                flex: 1, padding: '12px', background: 'none', border: 'none', borderBottom: activeTab === tab ? '2px solid var(--primary-color)' : '2px solid transparent',
                fontWeight: activeTab === tab ? 'bold' : 'normal', color: activeTab === tab ? 'var(--primary-color)' : '#666', cursor: 'pointer', whiteSpace: 'nowrap'
              }}
            >
              {tab === 'meeting' ? 'Details' : tab === 'payment_request' ? 'Request Payment' : tab === 'verify_proof' ? 'Verify Proof' : 'Discussion Thread'}
              {tab === 'verify_proof' && consultation.paymentProof?.verificationStatus === 'pending' && <span style={{ color: 'red', marginLeft: '5px' }}>●</span>}
            </button>
          ))}
        </div>

        <div className="modal-body" style={{ height: '60vh', overflowY: 'auto', padding: '1.5rem', display: 'flex', flexDirection: 'column' }}>
          
          {/* TAB 1: MEETING & STATUS */}
          {activeTab === 'meeting' && (
            <form id="meeting-form" onSubmit={handleMeetingSave}>
              <div className="form-row">
                <div className="form-group">
                  <label>Meeting Date & Time</label>
                  <input 
                    type="datetime-local" 
                    name="scheduledAt" 
                    value={meetingData.scheduledAt} 
                    onChange={(e) => setMeetingData({...meetingData, scheduledAt: e.target.value})} 
                  />
                </div>
                <div className="form-group">
                  <label>Overall Status</label>
                  
                  {/* --- UPDATE THIS SELECT ELEMENT --- */}
                  <select 
                    name="status" 
                    value={meetingData.status} 
                    onChange={(e) => {
                      const newStatus = e.target.value;
                      
                      // If status is changed to payment_pending, automatically clear meeting details
                      if (newStatus === 'payment_pending') {
                        setMeetingData({
                          ...meetingData, 
                          status: newStatus,
                          meetingLink: '', // Clear the link
                          scheduledAt: ''  // Clear the date
                        });
                        toast('Meeting details cleared as status is Payment Pending.', { icon: 'ℹ️' });
                      } else {
                        setMeetingData({...meetingData, status: newStatus});
                      }
                    }}
                  >
                    <option value="payment_pending">Payment Pending</option>
                    <option value="payment_submitted">Payment Submitted (Needs Verification)</option>
                    <option value="paid">Paid</option>
                    <option value="under_review">Under Review</option>
                    <option value="more_info_needed">More Info Needed</option>
                    <option value="scheduled">Scheduled</option>
                    <option value="completed">Completed (Awaiting Report)</option>
                    <option value="report_uploaded">Report Uploaded (Final)</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                  {/* ------------------------------------ */}
                  
                </div>
              </div>
              <div className="form-group">
                <label>Video Meeting Link</label>
                <input 
                  type="url" 
                  name="meetingLink" 
                  value={meetingData.meetingLink} 
                  onChange={(e) => setMeetingData({...meetingData, meetingLink: e.target.value})} 
                  placeholder="https://..." 
                  // Optionally disable the input if status is payment_pending
                  disabled={meetingData.status === 'payment_pending'} 
                />
              </div>
              <div className="form-group">
                <label>Report / Summary (Visible to Customer)</label>
                <textarea name="remediesSummary" rows="3" value={meetingData.remediesSummary} onChange={(e) => setMeetingData({...meetingData, remediesSummary: e.target.value})}></textarea>
              </div>
              <div className="form-group">
                <label>Private Notes</label>
                <textarea name="consultantNotes" rows="2" value={meetingData.consultantNotes} onChange={(e) => setMeetingData({...meetingData, consultantNotes: e.target.value})}></textarea>
              </div>
            </form>
          )}

          {/* TAB 2: PAYMENT REQUEST */}
          {activeTab === 'payment_request' && (
            <form id="payment-form" onSubmit={(e) => handlePaymentReqSave(e, false)}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                 <p style={{ margin: 0, color: '#666', fontSize: '0.9rem' }}>Send UPI/QR to customer for manual payment.</p>
                 <button type="button" onClick={loadDefaults} style={{ background: '#eef7f4', color: 'var(--vastu-green)', border: '1px solid var(--vastu-green)', padding: '5px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '0.8rem', fontWeight: 'bold' }}>Load My Defaults</button>
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Amount Due (₹) *</label>
                  <input type="number" value={paymentReq.amount} onChange={(e) => setPaymentReq({...paymentReq, amount: e.target.value})} required />
                </div>
                <div className="form-group">
                  <label>UPI ID</label>
                  <input type="text" value={paymentReq.upiId} onChange={(e) => setPaymentReq({...paymentReq, upiId: e.target.value})} placeholder="yourname@bank" />
                </div>
              </div>
              
              <div className="form-group" style={{ background: '#fdfdfd', padding: '1rem', borderRadius: '8px', border: '1px dashed #ccc' }}>
                <label>Upload New QR Code Image (Overrides current)</label>
                <input type="file" accept="image/*" onChange={(e) => setQrFile(e.target.files[0])} />
                
                {/* --- UPDATED TO SHOW IMAGE PREVIEWS --- */}
                <div style={{ marginTop: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                  {consultation.paymentInstructions?.qrCodeFile?.storagePath && !qrFile && (
                    <div style={{ padding: '1rem', background: '#f0fbf7', border: '1px solid #a8dccb', borderRadius: '8px' }}>
                      <strong style={{ color: 'var(--vastu-green)', display: 'block', marginBottom: '10px' }}>
                        <i className="fas fa-check-circle"></i> Active QR on this Consultation:
                      </strong>
                      {/* Render the image directly using the GCP storage path */}
                      <img src={consultation.paymentInstructions.qrCodeFile.storagePath} alt="Active QR" style={{ maxWidth: '200px', borderRadius: '8px', border: '1px solid #eee' }} />
                    </div>
                  )}
                  
                  {consultantProfile?.defaultPaymentInstructions?.qrCodeFile?.storagePath && (
                    <div style={{ padding: '1rem', background: '#f8f9fa', border: '1px solid #ddd', borderRadius: '8px' }}>
                      <strong style={{ color: '#555', display: 'block', marginBottom: '10px' }}>
                        <i className="fas fa-image"></i> Your Default QR:
                      </strong>
                      {/* Render the image directly using the GCP storage path */}
                      <img src={consultantProfile.defaultPaymentInstructions.qrCodeFile.storagePath} alt="Default QR" style={{ maxWidth: '200px', borderRadius: '8px', border: '1px solid #eee' }} />
                    </div>
                  )}
                </div>
                {/* -------------------------------------- */}
              </div>

              <div className="form-group">
                <label>Payment Link (Optional)</label>
                <input type="url" value={paymentReq.paymentLink} onChange={(e) => setPaymentReq({...paymentReq, paymentLink: e.target.value})} placeholder="https://..." />
              </div>
              <div className="form-group">
                <label>Payment Notes</label>
                <textarea rows="2" value={paymentReq.notes} onChange={(e) => setPaymentReq({...paymentReq, notes: e.target.value})} placeholder="Any extra instructions..."></textarea>
              </div>
            </form>
          )}

          {/* TAB 3: VERIFY PROOF */}
          {activeTab === 'verify_proof' && (
            <div>
              {consultation.paymentProof?.verificationStatus === 'not_submitted' ? (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
                  <i className="fas fa-clock" style={{ fontSize: '2rem', marginBottom: '1rem', color: '#ccc' }}></i>
                  <p>Customer has not submitted payment proof yet.</p>
                </div>
              ) : (
                <div style={{ background: '#fdfdfd', border: '1px solid #eee', padding: '1.5rem', borderRadius: '8px' }}>
                  <h4 style={{ marginTop: 0 }}>Customer Submission</h4>
                  <p><strong>Status:</strong> <span style={{ textTransform: 'uppercase', color: consultation.paymentProof.verificationStatus === 'pending' ? '#d97706' : 'var(--green)' }}>{consultation.paymentProof.verificationStatus}</span></p>
                  <p><strong>Transaction Ref/UTR:</strong> {consultation.paymentProof.transactionReference || 'N/A'}</p>
                  <p><strong>Submitted On:</strong> {new Date(consultation.paymentProof.submittedAt).toLocaleString()}</p>
                  
                  {consultation.paymentProof.proofFile && (
                    <button type="button" className="secondary-action-btn" style={{ marginTop: '1rem', width: '100%', borderColor: 'var(--primary-color)', color: 'var(--primary-color)', background: 'transparent' }} 
                            onClick={() => downloadAsset('proof', consultation.paymentProof.proofFile.originalName)}>
                      <i className="fas fa-download"></i> Download Proof Screenshot
                    </button>
                  )}

                  {consultation.paymentProof.verificationStatus === 'pending' && (
                    <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
                      <button onClick={() => handleVerify('verified')} className="btn-save" style={{ flex: 1, background: '#28a745' }} disabled={isSaving}>
                         <i className="fas fa-check"></i> Approve & Mark Paid
                      </button>
                      <button onClick={() => handleVerify('rejected')} className="btn-cancel" style={{ flex: 1, background: '#dc3545', color: 'white' }} disabled={isSaving}>
                         <i className="fas fa-times"></i> Reject
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* --- NEW TAB 4: DISCUSSION THREAD --- */}
          {activeTab === 'chat' && (
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              
              <div className="chat-messages-area" style={{ flexGrow: 1, overflowY: 'auto', padding: '1rem', background: '#f8f9fa', border: '1px solid #eee', borderRadius: '8px', marginBottom: '1rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {(consultation.messages || []).length === 0 ? (
                    <p style={{ textAlign: 'center', color: '#999', margin: 'auto', fontStyle: 'italic' }}>No messages yet. Say hello to your customer!</p>
                ) : (
                    consultation.messages.map((msg, idx) => {
                        const isMe = msg.senderId === user?.id;
                        return (
                            <div key={idx} style={{ display: 'flex', width: '100%', justifyContent: isMe ? 'flex-end' : 'flex-start' }}>
                                <div style={{ maxWidth: '80%', padding: '1rem', borderRadius: '12px', background: isMe ? '#eef7f4' : '#ffffff', border: isMe ? '1px solid #a8dccb' : '1px solid #eee', boxShadow: '0 2px 5px rgba(0,0,0,0.05)' }}>
                                    <div style={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#555', marginBottom: '0.5rem' }}>
                                        {msg.senderName} <small style={{ fontWeight: 'normal', color: '#888', textTransform: 'capitalize' }}>({msg.senderRole})</small>
                                    </div>
                                    {msg.text && <p style={{ margin: 0, color: '#333', lineHeight: '1.5', whiteSpace: 'pre-wrap', fontSize: '0.95rem' }}>{msg.text}</p>}
                                    {msg.attachment && (
                                        <button type="button" onClick={() => downloadAsset('attachment', msg.attachment.originalName, msg.attachment)} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', marginTop: '0.5rem', padding: '0.4rem 0.8rem', background: 'rgba(0,0,0,0.05)', border: '1px solid rgba(0,0,0,0.1)', borderRadius: '6px', fontSize: '0.85rem', cursor: 'pointer', color: '#333' }}>
                                            <i className="fas fa-image"></i> View Attachment
                                        </button>
                                    )}
                                    <div style={{ fontSize: '0.7rem', color: '#999', textAlign: 'right', marginTop: '0.5rem' }}>
                                        {new Date(msg.sentAt).toLocaleString([], { dateStyle: 'short', timeStyle: 'short' })}
                                    </div>
                                </div>
                            </div>
                        )
                    })
                )}
              </div>
              
              {/* Chat Input */}
              <div style={{ background: '#ffffff', padding: '10px', borderRadius: '8px', border: '1px solid #eee', boxShadow: '0 -2px 10px rgba(0,0,0,0.02)' }}>
                <textarea 
                    placeholder="Type message to customer..." 
                    rows="2"
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    style={{ width: '100%', padding: '0.8rem', border: '1px solid #ddd', borderRadius: '6px', resize: 'none', marginBottom: '10px', fontFamily: 'inherit' }}
                ></textarea>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <input 
                          type="file" 
                          accept="image/*,.pdf" 
                          id="consultant-chat-attach"
                          style={{ display: 'none' }}
                          onChange={(e) => setMessageFile(e.target.files[0])}
                      />
                    </div>
                    {/* --- APPLIED NEW CLASS HERE --- */}
                    <button 
                        className="btn-send-message" 
                        onClick={handleSendMessage}
                        disabled={isSendingMessage || (!messageText.trim() && !messageFile)}
                    >
                        {isSendingMessage ? 'Sending...' : 'Send Message'} <i className="fas fa-paper-plane" style={{marginLeft: '5px'}}></i>
                    </button>
                </div>
              </div>
            </div>
          )}
          {/* -------------------------------------- */}
        </div>

        {/* Footer Actions based on active tab */}
        <div className="modal-footer">
          <button type="button" className="btn-cancel" onClick={onClose} disabled={isSaving || isSendingMessage}>Close</button>
          
          {activeTab === 'meeting' && (
            <button type="submit" form="meeting-form" className="btn-save" disabled={isSaving}>{isSaving ? 'Saving...' : 'Save Details & Status'}</button>
          )}
          
          {activeTab === 'payment_request' && (
            <div style={{ display: 'flex', gap: '10px' }}>
                <button type="button" onClick={(e) => handlePaymentReqSave(e, true)} className="btn-save" style={{ background: '#16715c' }} disabled={isSaving}>
                    {isSaving ? 'Saving...' : 'Save & Set as My Default'}
                </button>
                <button type="submit" form="payment-form" className="btn-save" disabled={isSaving}>
                    {isSaving ? 'Saving...' : 'Send to Customer'}
                </button>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default ConsultantActionModal;